/**
 * useFunnelForm — estado e validação do formulário de funil (etapas + drag).
 */
import { useState, useEffect, useCallback } from 'react';
import { FunnelStage, Funnel } from '../../../../types';
import { FunnelStageData } from '../components/FunnelStageRow';
import { useDraggableList } from '../../../../hooks/useDraggableList';

interface UseFunnelFormProps {
    funnel?: Funnel | null;
    stages?: FunnelStage[];
}

export interface UseFunnelFormReturn {
    name: string;
    stageList: FunnelStageData[];
    draggedIndex: number | null;
    isDirty: boolean;
    setName: (name: string) => void;
    addStage: () => void;
    addStageAt: (index: number) => void;
    updateStageName: (index: number, name: string) => void;
    updateStageDeadline: (index: number, deadline: number) => void;
    updateStageConversionEvent: (index: number, event: string) => void;
    removeStage: (index: number) => void;
    handleDragStart: (index: number) => void;
    handleDragOver: (e: React.DragEvent, index: number) => void;
    handleDragEnd: () => void;
    getFormData: () => { name: string; stages: FunnelStageData[] };
    isValid: () => boolean;
    getErrors: () => string[];
}

const EMPTY_STAGE: FunnelStageData = {
    name: '',
    deadline: undefined,
    conversionEvent: '',
};

function toStageData(stage: FunnelStage): FunnelStageData {
    return {
        id: stage.id,
        name: stage.name,
        deadline: stage.deadline,
        conversionEvent: stage.conversionEvent || '',
        color: stage.color || null,
    };
}

export const useFunnelForm = ({ funnel, stages }: UseFunnelFormProps): UseFunnelFormReturn => {
    const [name, setNameState] = useState(funnel?.name || '');
    const [stageList, setStageList] = useState<FunnelStageData[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (stages && stages.length > 0 && !isInitialized) {
            setStageList(stages.map(toStageData));
            setIsInitialized(true);
        }
    }, [stages, isInitialized]);

    useEffect(() => {
        if (funnel) {
            setNameState(funnel.name);
            setIsInitialized(false);
            setIsDirty(false);
        }
    }, [funnel?.id]);

    const markDirty = useCallback(() => setIsDirty(true), []);

    const setName = useCallback((value: string) => {
        setNameState(value);
        setIsDirty(true);
    }, []);

    const patchStage = useCallback(<K extends keyof FunnelStageData>(
        index: number,
        key: K,
        value: FunnelStageData[K],
    ) => {
        setStageList((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [key]: value };
            return next;
        });
        setIsDirty(true);
    }, []);

    const addStage = useCallback(() => {
        setStageList((prev) => [...prev, { ...EMPTY_STAGE }]);
        setIsDirty(true);
    }, []);

    const addStageAt = useCallback((index: number) => {
        setStageList((prev) => {
            const next = [...prev];
            const clamped = Math.max(0, Math.min(index, next.length));
            next.splice(clamped, 0, { ...EMPTY_STAGE });
            return next;
        });
        setIsDirty(true);
    }, []);

    const updateStageName = useCallback(
        (index: number, value: string) => patchStage(index, 'name', value),
        [patchStage],
    );

    const updateStageDeadline = useCallback(
        (index: number, value: number) => patchStage(index, 'deadline', value),
        [patchStage],
    );

    const updateStageConversionEvent = useCallback(
        (index: number, value: string) => patchStage(index, 'conversionEvent', value),
        [patchStage],
    );

    const removeStage = useCallback((index: number) => {
        setStageList((prev) => prev.filter((_, i) => i !== index));
        setIsDirty(true);
    }, []);

    const {
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        draggedIndex,
    } = useDraggableList(stageList, setStageList, markDirty);

    const getFormData = useCallback(
        () => ({ name, stages: stageList }),
        [name, stageList],
    );

    const getErrors = useCallback(() => {
        const errors: string[] = [];
        if (!name.trim()) errors.push('Nome do funil é obrigatório');
        if (stageList.length === 0) errors.push('Adicione pelo menos uma etapa');
        if (stageList.some((s) => !s.name.trim())) {
            errors.push('Todas as etapas devem ter um nome');
        }
        return errors;
    }, [name, stageList]);

    const isValid = useCallback(() => getErrors().length === 0, [getErrors]);

    return {
        name,
        stageList,
        draggedIndex,
        isDirty,
        setName,
        addStage,
        addStageAt,
        updateStageName,
        updateStageDeadline,
        updateStageConversionEvent,
        removeStage,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        getFormData,
        isValid,
        getErrors,
    };
};

export default useFunnelForm;
